import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { countries } from 'utils/countries';

interface Props {
  value: string[];
  onChange: (_next: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
}

/**
 * Multi-select country picker over the full ISO list (utils/countries — the
 * same source the tariff/import tools use), styled to match CreatableSelect so
 * every searchable field in the Hub looks the same.
 *
 * `freeSolo` is on so a country the list doesn't carry (a territory, or a name
 * that arrived from an import) can still be typed and kept rather than silently
 * dropped when the field is edited.
 */
const CountryMultiSelect: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}) => {
  // Values already on the product that aren't in the canonical list still need
  // to render as chips, so fold them into the options.
  const options = React.useMemo(() => {
    const seen = new Set(countries.map((c) => c.toLowerCase()));
    const extra = value.filter((v) => v && !seen.has(v.toLowerCase()));
    return [...countries, ...extra];
  }, [value]);

  return (
    <Autocomplete
      multiple
      freeSolo
      fullWidth
      filterSelectedOptions
      handleHomeEndKeys
      value={value}
      options={options}
      aria-label={ariaLabel}
      onChange={(_e, next) => {
        // De-dupe case-insensitively: typing "japan" when "Japan" is already
        // chipped should be a no-op, not a second chip.
        const seen = new Set<string>();
        const out: string[] = [];
        (next as string[]).forEach((raw) => {
          const t = (raw || '').trim();
          if (t && !seen.has(t.toLowerCase())) {
            seen.add(t.toLowerCase());
            out.push(t);
          }
        });
        onChange(out);
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((t, i) => (
          <Chip
            {...getTagProps({ index: i })}
            key={t}
            label={t}
            size='small'
            sx={{
              background: 'var(--bg-colors)',
              color: 'var(--text-primary-color)',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              border: '1px solid var(--es-soft-border)',
              '& .MuiChip-deleteIcon': {
                color: 'var(--text-tertiary)',
                '&:hover': { color: 'var(--low-color)' },
              },
            }}
          />
        ))
      }
      componentsProps={{
        paper: {
          sx: {
            background: 'var(--bg-colors)',
            color: 'var(--text-primary-color)',
            border: '1px solid var(--es-soft-border)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
          },
        },
        popper: {
          sx: {
            zIndex: 1500,
            '& .MuiAutocomplete-option': {
              fontSize: '14px',
              color: 'var(--text-primary-color)',
            },
            '& .MuiAutocomplete-option.Mui-focused': {
              background: 'var(--bg-hover) !important',
            },
            '& .MuiAutocomplete-noOptions': { color: 'var(--text-tertiary)' },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          background: 'var(--bg-surface)',
          padding: '3px 8px',
          minHeight: '40px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          color: 'var(--text-primary-color)',
          '& fieldset': { borderColor: 'var(--es-soft-border)' },
          '&:hover fieldset': { borderColor: 'var(--action-color)' },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--action-color)',
            borderWidth: '1px',
          },
        },
        '& .MuiAutocomplete-input': {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
        },
        '& .MuiSvgIcon-root': { color: 'var(--text-secondary)' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : undefined}
        />
      )}
    />
  );
};

export default CountryMultiSelect;
